import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useDataset from "datasets/hooks/useDataset";
import useDatasetPermission from "datasets/hooks/useDatasetPermission";
import useDatasetReportFQNs from "datasets/pages/dashboards/hooks/useDatasetReportFQNs";
import useDatasetReports from "datasets/pages/dashboards/hooks/useDatasetReports";
import useReport from "datasets/pages/dashboards/hooks/useReport";
import { Loader2 } from "lucide-react";
import SigmaEmbed from "~/components/SigmaEmbed";
import useErrorToast from "~/contexts/error-toast";
import { apiMutate } from "~/utils/apiClient";
import { trackEvent } from "~/utils/monitoring";

const { VITE_CATALOG_API } = import.meta.env;

export const DatasetReport = () => {
  const { datasetId, reportId } = useParams();
  const { data: report, isLoading } = useReport();
  const { mutate: mutateDataset } = useDataset();
  const { mutate: mutateReports } = useDatasetReports();
  const { data: reportFQNs, mutate: mutateReportFQNs } = useDatasetReportFQNs();
  const hasEditPermission = useDatasetPermission("edit:catalog-report");
  const { showError } = useErrorToast();
  const navigate = useNavigate();

  useEffect(() => {
    if (datasetId && reportId) {
      trackEvent("dataset_dashboard_viewed", { dataset_id: datasetId, dashboard_id: reportId });
    }
  }, [datasetId, reportId]);

  const handleClose = () => navigate("..", { relative: "path" });

  const handleRemove = async () => {
    if (!report || !reportFQNs) {
      return;
    }
    const filtered = reportFQNs.filter((fqn) => fqn !== report.fullyQualifiedName);
    try {
      await apiMutate(`${VITE_CATALOG_API}/datasets/${datasetId}`, "PATCH", {
        reports: filtered,
      });
      await Promise.all([mutateDataset(), mutateReports(), mutateReportFQNs()]);
      handleClose();
    } catch (e: any) {
      showError(e.message ?? "Failed to remove dashboard");
    }
  };

  const messageHandler = async (e: MessageEvent) => {
    if (e.data.type === "workbook:ondelete") {
      await handleRemove();
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-full min-h-[50vh] w-full items-center justify-center">
        <Loader2 className="size-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!report) {
    return null;
  }

  return (
    <SigmaEmbed
      title={report.displayName}
      datasetId={datasetId}
      reportFqn={report.fullyQualifiedName}
      messageHandler={messageHandler}
      onClose={handleClose}
      allowEdits={hasEditPermission}
    />
  );
};
